import { useCallback, useMemo } from 'react';

import useAPI, { type State } from '~/common/hooks/useAPI';
import getProjectsAndIssueTypes, {
  type IssueType,
  type ProjectsAndIssueTypes
} from '~/common/utils/integrations-client/jira/getProjectsAndIssueTypes';

export interface IntegrationProjectsMethods {
  getProjectById: (id: string | null) => ProjectsAndIssueTypes | undefined;
  getIssueTypesByProjectId: (id: string | null) => IssueType[] | undefined;
  getIssueTypeById: (id: string | null) => IssueType | undefined;
}

const useIntegrationProjects = (
  enterpriseId: string,
  connectionId?: string,
  token?: string
): State<ProjectsAndIssueTypes[]> & IntegrationProjectsMethods => {
  const dataLoader = useCallback(() => {
    if (!connectionId) {
      return Promise.resolve([]);
    }

    return getProjectsAndIssueTypes({
      enterpriseId,
      connectionId,
      token
    });
  }, [connectionId, token, enterpriseId]);

  const { loading, error, data } = useAPI(dataLoader, !enterpriseId);

  const getProjectById = useCallback(
    (id: string | null): ProjectsAndIssueTypes | undefined =>
      id ? data?.find(p => p.id === id) : undefined,
    [data]
  );

  const getIssueTypesByProjectId = useCallback(
    (id: string | null): IssueType[] | undefined => {
      if (!id) {
        return;
      }

      const project = getProjectById(id);
      if (!project) {
        return;
      }

      return project.issueTypes;
    },
    [data]
  );

  const getIssueTypeById = useCallback(
    (id: string | null): IssueType | undefined =>
      id
        ? data
            ?.map(project => project.issueTypes || [])
            .flat()
            .find(issueType => issueType.id === id)
        : undefined,
    [data]
  );

  const results = useMemo(
    () => ({
      loading,
      error,
      data,
      getProjectById,
      getIssueTypesByProjectId,
      getIssueTypeById
    }),
    [
      loading,
      error,
      data,
      getProjectById,
      getIssueTypesByProjectId,
      getIssueTypeById
    ]
  );

  return results;
};

export default useIntegrationProjects;
